import type { Translations } from "../types";

const en: Translations = {
  // Header
  nav: {
    features: "Features",
    pricing: "Pricing",
    blog: "Blog",
    freeTools: "Free Tools",
    contact: "Contact",
    startFree: "Start Free",
    solutions: "Solutions",
    useCases: "Use Cases",
    switchToDark: "Switch to Dark Mode",
    switchToLight: "Switch to Light Mode",
    solutionsToolsDesc: "Calculators, generators & more",
    solutionsUseCasesDesc: "See Zapify in action",
  },

  // Hero
  hero: {
    rotatingWords: ["Auto DMs", "AI Replies"],
    headline: "Reply to Instagram Comments with",
    subheadline:
      "Auto-reply to comments with personalized DMs. Capture leads and grow your audience on autopilot.",
    cta: "Get Started Free",
    helperText: "Free to start",
    helperSeparator: "Easy setup",
    trustedBy: "Trusted by",
    creators: "5,000+ creators",
    dmsSent: "DMs Sent",
    dmsCount: "1,000,000+",
  },

  // Features
  features: {
    headingTitle: "Everything You Need to",
    headingHighlight: "Scale Your",
    headingDescription:
      "Zapify gives you complete control over your AI assistant & automation while saving hours of manual work.",
    cta: "Activate 30 Days Free Trial",
    items: [
      {
        title: "DM Automation",
        description:
          "Auto-respond to every Instagram comment in a DM, Turn DM chaos into qualified leads and sales.",
      },
      {
        title: "Ask to Follow",
        description:
          "Before sharing your exclusive content, we ask the user to follow you.",
      },
      {
        title: "AI Powered Replies",
        description:
          "Replies that understand the comment and sound like you.",
      },
      {
        title: "Fully Customizable Voice",
        description:
          "Pick tone & style, choose short/long replies, and control emojis + #hashtags.",
      },
      {
        title: "Global Language Support",
        description: "Speak their language, automatically.",
      },
      {
        title: "Collect Emails",
        description:
          "Build your email list automatically from Instagram comments.",
      },
      {
        title: "Team Access",
        description:
          "Invite team members to manage replies, leads, and conversations together.",
      },
      {
        title: "Multiple Instagram Accounts",
        description:
          "Connect and manage multiple Instagram accounts from one dashboard.",
      },
    ],
  },

  // Process
  process: {
    headingTitle: "Start Automating in",
    headingHighlight: "Minutes",
    headingDescription: "Follow these simple steps to automate your DMs",
    step: "Step",
    cta: "Start Automation",
    steps: [
      {
        title: "Select Triggers",
        description:
          "Choose which posts and keywords activate your automation.",
      },
      {
        title: "Automate Response",
        description:
          "Create personalized messages with links, offers, or content to send automatically.",
      },
      {
        title: "Viral",
        description:
          "Create more. Manage less. Automation does the rest.",
      },
    ],
  },

  // Engagement Growth
  engagement: {
    headingTitle: "Turn Instagram Engagement Into",
    headingHighlight: "Revenue on Autopilot",
    headingDescription:
      "Convert every comment into sales opportunities. Make money 24/7 while your AI assistant handles the conversations",
    card1Title: "Increase Link Clicks",
    card1TitleLine2: "Conversions",
    card1Description:
      "Turn comments into direct traffic. Automatically deliver your offers, products, and content directly to interested buyers through DMs.",
    card2Title: "24/7 AI Sales Agent",
    card2Description:
      "Never miss a comment. Auto-reply instantly and convert conversations into sales 24/7 while you sleep.",
  },

  // Graph
  graph: {
    headingHighlight: "Skyrocket your",
    headingHighlightWord: "Reach",
    headingDescription:
      "See how automated DMs and AI responses skyrocket your engagement rate, follower growth, and sales conversions by up to 10x",
    title: "Boost Engagement",
    by10x: "by 10x",
    subtitle: "Monetize your audience while you sleep",
    description:
      "Reply to every comment, convert them into DMs, and boost engagement automatically.",
    afterZapify: "After Zapify Pro",
    cta: "Boost Engagement Now",
  },

  // Testimonials
  testimonials: {
    headingTitle: "See Why Instagram Creators",
    headingHighlight: "Love",
    headingDescription:
      "Real success stories from influencers and brands who transformed their Instagram engagement and revenue with AI automation",
    followers: "Followers",
  },

  // FAQ
  faq: {
    headingTitle: "Frequently Asked",
    headingHighlight: "Questions",
    headingDescription:
      "Everything you need to know about getting started with Zapify",
    items: [
      {
        question: "Does Zapify work with any Instagram account?",
        answer:
          "Zapify supports Instagram Professional (Business or Creator) accounts. Personal accounts aren't supported by the API.",
      },
      {
        question: "Is Zapify accessible on mobile?",
        answer:
          "Yes, Zapify works great in your mobile browser, you can install the Browser App from here.",
      },
      {
        question: "Will automations sound robotic?",
        answer:
          "No. Zapify supports brand voice controls (tone, length, emoji use) so AI replies match your style. You can edit any message and add a human handoff.",
      },
      {
        question: "Is Zapify Meta approved?",
        answer:
          "Zapify is a Verified Tech Provider by Meta, we use Instagram official Graph APIs. We follow rate limits and policy guidelines to keep your account safe.",
      },
      {
        question: "Is Zapify safe to use?",
        answer:
          "Yes. All auth uses Meta's secure OAuth. We never ask for your Instagram password and we respect API permissions you grant.",
      },
      {
        question: "Is Zapify free?",
        answer:
          "We offer a free trial so you can test automations and see results before upgrading.",
      },
      {
        question: "Do you have a partner/affiliate program?",
        answer:
          "Yes. Approved partners earn commissions on new sign-ups and renewals. Apply on our Partner page.",
      },
      {
        question: "How do I contact support?",
        answer:
          "Use in-app chat or email us—most questions get a same-day response on business days. We also provide setup guides and best-practice templates.",
      },
    ],
  },

  // Verified By
  verified: {
    headingTitle: "Programs",
    headingHighlight: "& Partners",
    headingDescription:
      "Our Startup accepted by Stripe for Startups and Microsoft for Startups 2025",
    cta: "View Verification",
    badge: "Verified",
    heading: "Your Trusted Meta Verified Tech Provider",
    description: "Our company is officially accepted in Microsoft Startups for Founders and Stripe Startup Program.",
    missionNote: "See: Home → Hero and Creator Problems sections for our mission and value.",
    close: "Close",
  },

  // Stats
  stats: {
    dmsSent: "DM's Sent",
    followersGained: "Followers Gained",
    commentsSent: "Comments Sent",
    countries: "Countries",
  },

  // Pricing
  pricing: {
    headingTitle: "Find the right plan for your",
    headingHighlight: "needs",
    headingDescription:
      "Whether you're an individual content creator, a brand, a business, or an agency, we've got you covered.",
    monthly: "Monthly",
    yearly: "Yearly",
    get2MonthsFree: "Get 2 Months Free",
    freeTrial: "Free Trial",
    freeTrialDesc: "Perfect to get started with Zapify.",
    starter: "Starter",
    starterDesc: "Best for individual content creators.",
    professional: "Professional",
    professionalDesc: "Best for startups and small brands.",
    subscribe: "Subscribe",
    startFreeTrial: "Start 30 Days Free Trial",
    free: "FREE",
    noCreditCard: "No Credit Card",
    bestSeller: "Best Seller",
    perMonth: "/month",
    save: "Save",
    unlimited: "Unlimited",
    limited: "Limited",
    email: "Email",
    priority: "Priority",
    automations: "Automations",
    dms: "DMs",
    aiReplies: "AI Replies",
    askToFollow: "Ask to Follow",
    emailCollection: "Email Collection",
    igProfile: "IG Profile",
    teamAccess: "Team Access",
    support: "Support",
    // Category headers
    automationPower: "Automation Power",
    aiCapabilities: "AI Capabilities",
    leadGeneration: "Lead Generation",
    analyticsTeam: "Analytics & Team",
    supportPlatform: "Support & Platform",
    soonBadge: "Soon",
    // AUTOMATION POWER features
    activeAutomations: "Active automations",
    instagramAccounts: "Instagram accounts",
    customTriggerWords: "Custom trigger words",
    instantDMReplies: "Instant DM replies",
    commentToDMAutomation: "Comment-to-DM automation",
    storyReplyAutomation: "Story reply automation",
    welcomeMessageAutomation: "Welcome message automation",
    smartGreetingMessage: "Smart greeting message",
    unlimitedPostsStories: "Unlimited posts & stories automated",
    flexiblePostTargeting: "Flexible post targeting (next, any, multiple)",
    linkInDMCTAButtons: "Link-in-DM & CTA buttons",
    followGatedDMsLinks: "Follow-gated DMs & links",
    instagramSafetyLimits: "Instagram safety limits",
    customFlowBuilder: "Custom flow builder",
    // AI CAPABILITIES features
    aiCommentReplies: "AI comment replies",
    aiPoweredInstantReplies: "AI-powered instant replies",
    aiTrainedOnBrand: "AI trained on your brand",
    // LEAD GENERATION features
    storedContacts: "Stored contacts",
    emailCaptureInsideDMs: "Email capture inside DMs",
    // ANALYTICS & TEAM features
    instagramInsights: "Instagram insights",
    monthlyPerformanceReports: "Monthly performance reports",
    teamMembers: "Team members",
    // SUPPORT & PLATFORM features
    customerSupport: "Customer support",
    languageSupport: "Language support",
    platformBranding: "Platform branding",
    offerTitle: "Limited Time Offer",
    offerDesc: "Register today and get",
    offerBadge: "10% OFF TODAY",
    limitedSpots: "Limited Spots",
    offerEndsIn: "Offer ends in",
    days: "Days",
    hours: "Hours",
    minutes: "Minutes",
    seconds: "Seconds",
    claimed: "claimed",
    secureCheckout: "Secure checkout",
    noCreditCardRequired: "No credit card required",
    copied: "COPIED!",
    comparePlansEyebrow: "Built for creators, Brands & Agencies",
    comparePlansTitle: "Start Free. Scale When you're ready.",
    comparePlansSubtitle: "Compare plans and choose the right level of automation for your growth.",
    compareFeatureColumn: "Feature",
    plansCtaBadgeLead: "Start Smart.",
    plansCtaBadgeEmphasis: "Create More.",
    plansCtaTitleLead: "Not built to keep you.",
    plansCtaTitleGradient: "Built to free you.",
    plansCtaSubtitle:
      "Most growth tools teach dependency. We built the opposite: automate the noise, stay where it matters, and turn real chats into momentum.",
    plansCtaSubtitleParagraph2:
      "When it runs so quietly you barely think about us—that's how we know we did our job.",
    plansCtaBullet1: "No credit card required",
    plansCtaBullet3: "Cancel anytime, no long-term",
    plansCtaTrust: "Trusted by 5,000+ creators worldwide",
    signupMixEyebrow: "Signup snapshot",
    signupMixCountLabel: "sign-ups in this sample",
    signupMixLeadTitle: "How people started",
    signupMixLeadSubtitle:
      "Most begin on a free trial. The rest split between Starter and jumping straight into Professional.",
    signupMixLabelFreeTrial: "Free trial",
    signupMixLabelStarter: "Starter",
    signupMixLabelProfessional: "Professional",
  },

  // CTA
  cta: {
    readyToTransform: "Ready to Transform Your Instagram?",
    joinThousands:
      "Join thousands of creators & brands using Zapify to grow followers, boost engagement & save time.",
    startForFree: "Start for Free",
  },

  // Sticky
  sticky: {
    instantlyDm: "Instantly DM anyone who comments on your Instagram",
    limitedOffer: "Limited Time Offer - 10% OFF",
    endsIn: "Ends in",
    getStartedFree: "Get Started Free",
    start: "Start",
  },

  // Contact Page
  contactPage: {
    heading: "Contact Support",
    subtitle:
      "We're here to help you grow. Reach out to us for any questions or support.",
    connectWithUs: "Connect with Us",
    connectDescription: "Choose your preferred way to reach out",
    emailUs: "Email Us",
    emailResponse: "We'll respond within 24 hours",
    support: "Support",
    getInstantHelp: "Get instant help on",
    scheduleMeet: "Schedule Meet",
    followUs: "Follow Us",
    stayUpdated: "Stay updated with latest news",
    responseTime: "Response within 24h",
  },

  // Contact Form
  contactForm: {
    heading: "Send Us a Message",
    description: "Fill out the form below and we'll get back to you",
    fullName: "Full Name",
    fullNamePlaceholder: "Enter your full name",
    emailAddress: "Email Address",
    emailPlaceholder: "Enter your email address",
    subject: "Subject",
    subjectPlaceholder: "What's this about?",
    message: "Message",
    messagePlaceholder: "Tell us more about your inquiry...",
    sending: "Sending...",
    sendMessage: "Send Message",
    successMessage: "Message sent successfully! We'll get back to you soon.",
    errorMessage: "Something went wrong. Please try again.",
  },

  // Blog
  blog: {
    insightsStrategy: "Insights & Strategy",
    masterTheArt: "Master the Art of",
    instagramGrowth: "Instagram Growth",
    blogSubtitle: "Unlock expert playbooks, automation secrets, and verified strategies to turn your comments into customers.",
    searchPlaceholder: "Search for strategies, guides...",
    followForUpdates: "Follow for updates",
    latestStories: "Latest Stories",
    minRead: "min read",
    readStory: "Read Story",
    featuredStory: "Featured Story",
    freshDrop: "Fresh Drop",
    readArticle: "Read Article",
    noArticlesFound: "No articles found",
    noArticlesDescription: "We couldn't find any articles matching your search. Try adjusting your filters or check back later.",
    clearSearch: "Clear Search",
  },

  // Referral
  referral: {
    heading: "Join Our Referral",
    headingHighlight: "Program",
    description: "Refer your friends to Zapify and earn 20% of the total revenue they spend on a paid subscription during their first year.",
    proSubscriber: "Zapify Pro subscriber",
    minimumDms: "sent a minimum of 100 DMs",
    joinProgram: "Join the Referral Program",
    termsOfService: "terms of service.",
    step1: "Step 1",
    step1Title: "Get Your Referral Link",
    step1Desc: "Sign up in minutes — no approvals needed! If you're a Pro subscriber with 100+ DMs sent, you're eligible to start earning.",
    referralLink: "Referral Link",
    copy: "Copy",
    step2: "Step 2",
    step2Title: "Share & Promote",
    step2Desc: "Copy your link and share it anywhere — Instagram, TikTok, YouTube, email, or DMs. The more you share, the more you earn!",
    step3: "Step 3",
    step3Title: "Earn 20% Commission",
    step3Desc: "When someone upgrades to a paid plan using your link, you earn 20% of their payments for the first year — paid monthly!",
    quickStats: "Quick Stats",
    clicks: "Clicks",
    converted: "Converted",
    total: "Total",
    pending: "Pending",
    faqHeading: "Referral Program",
    faqSubheading: "The most popular questions about our referral program",
    faq1Q: "What is the Zapify Referral Program?",
    faq1A: "The Zapify Referral Program allows you to earn commissions by referring new users to our platform. When someone signs up for a paid plan using your unique referral link, you earn 20% of their subscription fees for the first year.",
    faq2Q: "Who can join the referral program?",
    faq2A: "To join, you must be a Zapify Pro subscriber and have sent at least 100 DMs using our automation tools. This ensures our affiliates are active users who can genuinely vouch for the platform.",
    faq3Q: "How and when do I get paid?",
    faq3A: "Commissions are paid out monthly via PayPal once your earnings reach the minimum payout threshold of $50. Payments are processed approximately 30 days after the end of the month in which they were earned.",
    faq4Q: "Is there a limit to how much I can earn?",
    faq4A: "No! There is no cap on your earnings. The more successful referrals you make, the more you earn.",
  },

  // Tools Page
  toolsPage: {
    heading: "Free Instagram",
    headingHighlight: "Tools",
    description:
      "Calculators, generators, formatters, and more — all free, no signup required. Built for creators who want to grow smarter.",
    searchPlaceholder: "Search tools...",
    all: "All",
    noToolsFound: "No tools found matching your search.",
    free: "Free",
    comingSoon: "Coming Soon",
    relatedTools: "Related Tools",
    relatedToolsDesc: "More free tools to grow your Instagram",
    viewAllTools: "View all free tools",
    frequentlyAsked: "Frequently Asked",
    questionsHighlight: "Questions",
    faqDescription: "Everything you need to know about the",
  },

  // Use Case
  useCase: {
    heading: "What Can You Automate with",
    headingHighlight: "Zapify?",
    description: "From driving sales to collecting emails — see how creators and brands use Zapify to automate their Instagram DMs and grow on autopilot.",
    startForFree: "Start For Free",
    learnMore: "Learn more",
    bottomCtaHeading: "Ready to automate your Instagram DMs?",
    bottomCtaDesc: "Join thousands of creators who use Zapify to turn conversations into sales — automatically.",
    freeToStart: "Free to start · Easy setup",
  },

  // Footer
  footer: {
    description:
      "The #1 Instagram DM automation tool trusted by 5,000+ creators worldwide.",
    product: "Product",
    company: "Company",
    compare: "Compare",
    howItWorks: "How it works",
    termsOfService: "Terms of Service",
    privacyPolicy: "Privacy Policy",
    refundPolicy: "Refund Policy",
    contactUs: "Contact Us",
    referralProgram: "Referral Program",
    copyright: "All rights reserved.",
    builtWith: "Built with \u2764\uFE0F for creators",
    useCasesHeading: "Use Cases",
    allUseCases: "All Use Cases",
    sendLinksInDms: "Send Links in DMs",
    autoRespondComments: "Auto-Respond Comments",
    collectEmails: "Collect Emails",
    storyReplies: "Story Replies",
  },

  // Auth
  auth: {
    welcomeToZapify: "Welcome to Zapify",
    setItUpOnce: "Set it up once. Let AI do the rest.",
    recommended: "Recommended",
    continueWithInstagram: "Continue with Instagram",
    continueWithGoogle: "Continue with Google",
    redirecting: "Redirecting...",
    orContinueWithEmail: "or continue with email",
    emailAddress: "Email address",
    emailRequired: "Email is required",
    validEmail: "Please enter a valid email",
    sendingCode: "Sending code...",
    startFree: "Start Free",
    enterVerificationCode: "Enter verification code",
    weSentCode: "We sent a 6-digit code to",
    otpRequired: "OTP is required",
    otpDigits: "OTP must be 6 digits",
    verifying: "Verifying...",
    verifyAndContinue: "Verify and continue",
    useDifferentEmail: "Use different email",
    agreeToTerms: "By continuing, you agree to our",
    termsOfService: "Terms of Service",
    and: "and",
    privacyPolicy: "Privacy Policy",
    haveDiscountCode: "Have a discount code?",
    discountCode: "Discount Code",
    enterCode: "Enter code",
    apply: "Apply",
    applied: "Applied",
    congratsDiscount: "Congrats! You've got an extra 20% off.",
    invalidCode: "Invalid discount code",
    redirectingToDashboard: "Redirecting to dashboard...",
    referralBannerText: "Congrats! You've got an extra 20% off.",
    extendedTrialBannerText: "Congrats! You've unlocked {days} days of free trial.",
  },

  // Comparison
  comparison: {
    updatedDate: "Updated March 2026",
    startFreeNoCreditCard: "Start Free — No Credit Card",
    seeFullComparison: "See Full Comparison",
    quickAnswer: "Quick Answer",
    featureComparison: "Feature-by-Feature Comparison",
    samePriceTitle: "Same price, but what do you actually get?",
    aiVsKeywords: "AI Intent Recognition vs Basic Keyword Templates",
    userComments: "What users actually type in comments:",
    bottomCtaHeading: "Ready to try the smarter alternative?",
    bottomCtaDesc: "Join thousands of creators who switched to Zapify for AI-powered Instagram automation.",
    getStarted: "Get Started Free",
    freeToStart: "Free to start · No credit card required",
    feature: "Feature",
    category: "Category",
    pricingTiers: "Pricing Tiers",
    faq: "Frequently Asked Questions",
  },

  // Legal
  legal: {
    lastUpdated: "Last Updated",
    haveQuestions: "Have questions?",
    contactEmail: "Feel free to reach out to us at",
    returnToWebsite: "Return to Website",
    termsOfService: "Terms of Service",
    privacyPolicy: "Privacy Policy",
    refundPolicy: "Refund Policy",
    dataDeletion: "Data Deletion Policy",
  },

  // Use Case Layout
  useCaseLayout: {
    startForFree: "Start For Free",
    freeToStart: "Free to start · Easy setup",
    frequentlyAsked: "Frequently Asked",
    questionsHighlight: "Questions",
    readyToAutomate: "Ready to automate your Instagram DMs?",
    readyToAutomateDesc: "Join thousands of creators who use Zapify to turn conversations into sales — automatically.",
    trigger: "Trigger:",
    response: "Response:",
    exploreMore: "Explore More",
  },

};

export default en;
